import React, { useState } from 'react';
import {
  HomeModernIcon,
  BanknotesIcon,
  UserPlusIcon,
  PencilSquareIcon,
  ArchiveBoxArrowDownIcon,
  ArrowPathIcon,
  ArrowUturnUpIcon,
  MegaphoneIcon,
  Bars3Icon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import type {
  ListingIntent,
  ManagedProperty,
  PropertyProgressStatus,
} from '../../../components/property/property-card/PropertyCardTypes';
import { formatAvailableDate as formatDate } from '../../../utility/date-formatter';
import { Menu, Transition } from '@headlessui/react';
import BroadcastModal, { TenancyRecipient } from '../../messages/BroadcastModal';
import toast from 'react-hot-toast';
import { useAuth } from '../../../auth/firebaseAuth';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../../auth/firebaseConfig';
import { Fragment } from 'react';

type ManagedPropertyWithProgress = ManagedProperty & {
  listingIntent?: ListingIntent;
  propertyProgressStatus?: PropertyProgressStatus;
};

interface ManagedPropertyCardProps {
  property: ManagedPropertyWithProgress;
  unarchivingPropertyId: number | null;
  openAddTenantModal: (property: ManagedPropertyWithProgress, propertyTitle?: string) => void;
  openEditModal: (property: ManagedPropertyWithProgress) => void;
  openArchiveModal: (property: ManagedPropertyWithProgress) => void;
  submitUnarchiveProperty: (property: ManagedPropertyWithProgress) => void;
}

const ManagedPropertyCard: React.FC<ManagedPropertyCardProps> = ({
  property,
  unarchivingPropertyId,
  openAddTenantModal,
  openEditModal,
  openArchiveModal,
  submitUnarchiveProperty,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [tenancies, setTenancies] = useState<TenancyRecipient[]>([]);
  const [loadingTenancies, setLoadingTenancies] = useState(false);

  // Helper to determine if address and title are identical to avoid duplication
  const isTitleAddress = property.title?.toLowerCase() === property.address?.toLowerCase();

  const fetchTenants = async () => {
    if (tenancies.length > 0) return;
    setLoadingTenancies(true);
    try {
      const token = await currentUser?.getIdToken();
      if (!token) return;
      const response = await fetch(
        `${CLOUD_FUNCTIONS_BASE_URL}/getTenantsForProperty?listingId=${property.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.data && Array.isArray(data.data)) {
          setTenancies(
            data.data.map((t: any) => ({
              id: t.id,
              name: t.tenantInfo.name,
            }))
          );
        }
      }
    } catch (error) {
      console.error('Failed to fetch tenants', error);
    } finally {
      setLoadingTenancies(false);
    }
  };

  const handleSendBroadcast = async (
    subjectArg?: string,
    messageArg?: string,
    tenancyId?: string
  ) => {
    const finalSubject = (subjectArg ?? broadcastSubject).trim();
    const finalMessage = (messageArg ?? broadcastMessage).trim();

    if (!currentUser || !property || !finalSubject || !finalMessage) {
      toast.error('Please fill in both subject and message');
      return;
    }
    setSendingBroadcast(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/sendBroadcastToTenants`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          listingId: property.id,
          tenancyId,
          subject: finalSubject,
          message: finalMessage,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to send broadcast');
      setShowBroadcastModal(false);
      setBroadcastSubject('');
      setBroadcastMessage('');
      toast.success(`Broadcast sent successfully to ${data.data?.recipientCount ?? 'tenants'}!`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to send broadcast');
    } finally {
      setSendingBroadcast(false);
    }
  };

  // Subtle tooltip content for progress status badge
  const progressInfo = property.propertyProgressStatus
    ? property.propertyProgressStatus === 'new'
      ? { label: 'New', tooltip: 'Added to portfolio' }
      : property.propertyProgressStatus === 'viewing-in-progress'
        ? {
            label: 'Viewing In Progress',
            tooltip: 'Viewing scheduled or ongoing',
          }
        : {
            label: 'Sale Agreed',
            tooltip: 'Offer accepted; awaiting completion',
          }
    : null;

  return (
    <>
      <div className="relative flex flex-col gap-5 rounded-3xl border border-gray-100 bg-white p-5 shadow hover:border-blue-100 hover:shadow-xl hover:z-30 focus-within:z-30 md:flex-row md:items-start">
        {/* Image Section */}
        <div className="h-48 w-full shrink-0 overflow-hidden rounded-2xl bg-gray-100 md:h-32 md:w-44">
          {property.images && property.images.length > 0 ? (
            <img
              src={property.images[0]}
              alt={property.title || property.type}
              className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-gray-300">
              <HomeModernIcon className="h-12 w-12" />
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="flex flex-1 flex-col gap-2">
          {/* Header: Title + Status Badge */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 capitalize leading-tight">
                {property.title || property.type}
              </h3>
              {/* Only show address if it's different from the title */}
              {!isTitleAddress && (
                <div className="mt-1 flex items-center gap-1 text-sm text-gray-500 capitalize">
                  <MapPinIcon className="h-3.5 w-3.5" />
                  {property.address || property.location}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                  property.status === 'managed'
                    ? 'bg-blue-50 text-blue-700 ring-blue-600/20'
                    : 'bg-gray-50 text-gray-600 ring-gray-500/10'
                }`}
              >
                {property.status === 'managed' ? 'Active' : 'Archived'}
              </span>
              {property.status === 'managed' && property.propertyProgressStatus && (
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                    property.propertyProgressStatus === 'new'
                      ? 'bg-violet-50 text-violet-700 ring-violet-600/20'
                      : property.propertyProgressStatus === 'viewing-in-progress'
                        ? 'bg-sky-50 text-sky-700 ring-sky-600/20'
                        : 'bg-green-50 text-green-700 ring-green-600/20'
                  }`}
                  title={progressInfo?.tooltip}
                  aria-label={progressInfo?.tooltip}
                >
                  {progressInfo?.label}
                </span>
              )}
            </div>
          </div>

          {/* Metadata Pills */}
          <div className="mt-2 flex flex-wrap gap-2 text-xs font-medium text-gray-600">
            <span className="flex items-center rounded-md bg-gray-50 px-2 py-1 ring-1 ring-inset ring-gray-500/10 capitalize">
              {property.type}
            </span>
            <span className="flex items-center rounded-md bg-gray-50 px-2 py-1 ring-1 ring-inset ring-gray-500/10">
              {property.bedrooms} Bed
            </span>
            <span className="flex items-center rounded-md bg-gray-50 px-2 py-1 ring-1 ring-inset ring-gray-500/10">
              {property.bathrooms} Bath
            </span>
            {property.listingIntent && (
              <span className="flex items-center rounded-md bg-blue-50 px-2 py-1 text-blue-700 ring-1 ring-inset ring-blue-600/20 capitalize">
                {property.listingIntent}
              </span>
            )}
            {property.size && (
              <span className="flex items-center rounded-md bg-gray-50 px-2 py-1 ring-1 ring-inset ring-gray-500/10">
                {property.size} sqm
              </span>
            )}
            {property.berRating && (
              <span className="flex items-center rounded-md bg-emerald-50 px-2 py-1 text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                BER {property.berRating}
              </span>
            )}
          </div>

          {/* Optional: Notes */}
          {property.notes && (
            <p className="mt-2 line-clamp-1 text-sm text-gray-400 italic">"{property.notes}"</p>
          )}

          {/* Helper Text: List Property CTA */}
          {property.status === 'managed' && (
            <p className="mt-3 text-xs text-gray-400 border-l-2 border-blue-200 pl-2.5">
              💡 <span className="font-medium text-gray-500">Find tenants:</span> Click "Actions" →
              "List Property for FREE" to showcase this property publicly
            </p>
          )}

          {/* Archived Metadata */}
          {property.status === 'managed-archived' && property.archivedAt && (
            <p className="mt-2 text-xs text-gray-400">
              Archived on {formatDate(property.archivedAt)}{' '}
              {property.archiveReason && `• ${property.archiveReason}`}
            </p>
          )}
        </div>

        {/* Action Button & Dropdown */}
        <div className="flex md:flex-col items-end justify-start">
          {!showBroadcastModal && (
            <Menu as="div" className="relative inline-block text-left">
              <div>
                <Menu.Button className="inline-flex w-full items-center justify-center gap-x-1.5 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                  Actions
                  <Bars3Icon className="-mr-0.5 h-5 w-5 text-gray-400" aria-hidden="true" />
                </Menu.Button>
              </div>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                {/* Width increased to w-64 to prevent text cut-off */}
                <Menu.Items className="absolute right-0 z-10 mt-2 w-64 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {/* PRIMARY CTA: List Property */}
                  <div className="bg-gradient-to-r from-blue-50 to-blue-25 px-4 py-3">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() =>
                            navigate('/add-listing', {
                              state: { prefillData: property },
                            })
                          }
                          className={`w-full rounded-lg font-semibold text-white text-sm flex items-center gap-2 py-2.5 px-3 transition-all ${
                            active
                              ? 'bg-blue-700 shadow-md scale-105'
                              : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        >
                          <MegaphoneIcon className="h-5 w-5 shrink-0" />
                          List Property for FREE
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                  {/* Group 1: Daily Management */}
                  <div className="py-1">
                    {property.status === 'managed' && (
                      <>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => navigate(`/property/${property.id}/tenants`)}
                              className={`${
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                              } group flex w-full items-center px-4 py-2 text-sm`}
                            >
                              <UserPlusIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                              View Tenants
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() =>
                                openAddTenantModal(property, property.title || property.type)
                              }
                              className={`${
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                              } group flex w-full items-center px-4 py-2 text-sm`}
                            >
                              <UserPlusIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                              Add Tenancy
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() =>
                                navigate('/property-tools/rent-tracker', {
                                  state: { propertyId: property.id },
                                })
                              }
                              className={`${
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                              } group flex w-full items-center px-4 py-2 text-sm`}
                            >
                              <BanknotesIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                              Track Rent
                            </button>
                          )}
                        </Menu.Item>
                      </>
                    )}
                  </div>

                  {/* Group 2: Marketing & Comms */}
                  <div className="py-1">
                    {property.status === 'managed' && (
                      <>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => {
                                fetchTenants();
                                setShowBroadcastModal(true);
                              }}
                              className={`${
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                              } group flex w-full items-center px-4 py-2 text-sm`}
                            >
                              <ChatBubbleLeftRightIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                              Message Tenants
                            </button>
                          )}
                        </Menu.Item>
                      </>
                    )}
                  </div>

                  {/* Group 3: Admin / Edit / Archive */}
                  <div className="py-1">
                    {property.status === 'managed' && (
                      <>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => openEditModal(property)}
                              className={`${
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                              } group flex w-full items-center px-4 py-2 text-sm`}
                            >
                              <PencilSquareIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                              Edit
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => openArchiveModal(property)}
                              className={`${
                                active ? 'bg-rose-50 text-rose-900' : 'text-gray-700'
                              } group flex w-full items-center px-4 py-2 text-sm`}
                            >
                              <ArchiveBoxArrowDownIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-rose-500" />
                              Archive
                            </button>
                          )}
                        </Menu.Item>
                      </>
                    )}

                    {property.status === 'managed-archived' && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => submitUnarchiveProperty(property)}
                            disabled={unarchivingPropertyId === property.id}
                            className={`${
                              active ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'
                            } group flex w-full items-center px-4 py-2 text-sm ${
                              unarchivingPropertyId === property.id
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            }`}
                          >
                            {unarchivingPropertyId === property.id ? (
                              <ArrowPathIcon className="mr-3 h-5 w-5 animate-spin text-gray-400" />
                            ) : (
                              <ArrowUturnUpIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-emerald-500" />
                            )}
                            Unarchive
                          </button>
                        )}
                      </Menu.Item>
                    )}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          )}
        </div>
      </div>

      <BroadcastModal
        isOpen={showBroadcastModal}
        onClose={() => setShowBroadcastModal(false)}
        recipientName={property.title || 'All tenants'}
        initialSubject={broadcastSubject}
        onSend={(s, m, tId) => handleSendBroadcast(s, m, tId)}
        isSending={sendingBroadcast}
        tenancies={tenancies}
      />
    </>
  );
};

export default ManagedPropertyCard;
